import { LayoutItem, verticalCompactor } from 'react-grid-layout';
import { IWidgetsData, WidgetsLayouts } from './IWidgetsData';

// Версия модели сетки. Увеличивается при изменении сетки, требующем нормализации сохранённых
// раскладок. Миграция ступенчатая (по диапазонам версий), чтобы корректно подхватывать данные,
// застрявшие на промежуточной версии.
//   v1 (и раньше) — старая 12-колоночная сетка (rowHeight 20 + margin 10).
//   v2 — раскладка уже в новой квадратной сетке по ширине, но возможны вертикальные наложения.
//   v3 — размеры подтянуты под актуальные min/max, наложения убраны вертикальной компактацией.
export const GRID_VERSION = 3;

// Колонки прежней (до квадратной сетки) раскладки — по ним считаем масштаб по ширине.
const OLD_COLS: Record<string, number> = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const NEW_COLS: Record<string, number> = { lg: 40, md: 28, sm: 22, xs: 16, xxs: 10 };

// Прежняя сетка: rowHeight=20 + вертикальный margin=10 → шаг строки 30px. Новая клетка ~40px,
// margin=0, поэтому в высоте/позиции по Y переводим пиксели старого шага в новые клетки.
const OLD_ROW_STEP = 30;
const OLD_ROW_HEIGHT = 20;
const NEW_CELL = 40;

// Ограничения размеров виджетов на момент v3 (snapshot из билдеров). Держим здесь, чтобы при
// миграции подтянуть размеры под них ДО компактации: иначе applyBuilderConstraints раздул бы
// высоту уже после раскладки и вернул наложения. Это исторический snapshot — менять только
// вместе с подъёмом GRID_VERSION.
const NEW_CONSTRAINTS: Record<string, { minW: number; maxW: number; minH: number; maxH: number }> = {
    'calendar-widget': { minW: 20, maxW: 40, minH: 16, maxH: 30 },
    'favorites-widget': { minW: 20, maxW: 40, minH: 10, maxH: 15 },
    'drafts-widget': { minW: 20, maxW: 40, minH: 10, maxH: 15 },
    'text-widget': { minW: 7, maxW: 13, minH: 3, maxH: 9 },
};

const round = Math.round;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const newColsOf = (bp: string) => NEW_COLS[bp] ?? NEW_COLS.lg;
const constraintsFor = (i: string) =>
    Object.entries(NEW_CONSTRAINTS).find(([id]) => (i || '').startsWith(id))?.[1] ?? null;

// Похоже ли на старую 12-колоночную раскладку: в lg есть элементы и ни один не выходит за
// 12 колонок. Вторичная страховка к gridVersion на случай, если бэк не сохранит поле версии.
function looksLikeOldGrid(layouts: WidgetsLayouts): boolean {
    const lg = layouts.lg;
    if (!lg || lg.length === 0) return false;
    const maxRight = lg.reduce((m, it) => Math.max(m, it.x + it.w), 0);
    return maxRight <= OLD_COLS.lg;
}

// Масштаб из старой 12-колоночной сетки в новую квадратную (без компактации — ею занят
// отдельный шаг). Ширина — по доле колонок, высота/Y — по пикселям старого шага строки.
function scaleItems(items: LayoutItem[], bp: string): LayoutItem[] {
    const scaleX = newColsOf(bp) / (OLD_COLS[bp] ?? OLD_COLS.lg);
    return items.map((it) => {
        const heightPx = OLD_ROW_HEIGHT * it.h + (it.h - 1) * (OLD_ROW_STEP - OLD_ROW_HEIGHT);
        const topPx = it.y * OLD_ROW_STEP;
        return {
            ...it,
            x: Math.max(0, round(it.x * scaleX)),
            w: Math.max(1, round(it.w * scaleX)),
            y: round(topPx / NEW_CELL),
            h: Math.max(1, round(heightPx / NEW_CELL)),
        };
    });
}

// Подтягивает w/h под актуальные min/max и держит x в границах сетки, затем убирает вертикальные
// наложения компактацией. Clamp размеров ДО compact обязателен: иначе applyBuilderConstraints,
// расширив высоту после раскладки, вернул бы наложения.
function normalizeAndCompact(items: LayoutItem[], bp: string): LayoutItem[] {
    const newCols = newColsOf(bp);
    const clamped = items.map((it) => {
        const c = constraintsFor(it.i);
        const w = Math.min(newCols, c ? clamp(it.w, c.minW, c.maxW) : Math.max(1, it.w));
        const h = c ? clamp(it.h, c.minH, c.maxH) : Math.max(1, it.h);
        const x = Math.min(newCols - w, Math.max(0, it.x));
        return { ...it, x, w, h };
    });
    return verticalCompactor.compact(clamped, newCols) as LayoutItem[];
}

function mapLayouts(
    layouts: WidgetsLayouts,
    fn: (items: LayoutItem[], bp: string) => LayoutItem[],
): WidgetsLayouts {
    const out: WidgetsLayouts = {};
    Object.keys(layouts).forEach((bp) => {
        const items = layouts[bp];
        out[bp] = items ? fn(items, bp) : [];
    });
    return out;
}

/**
 * Приводит сохранённую раскладку к актуальной версии сетки. Ступенчато:
 *   - из старой 12-колоночной сетки масштабируем ширину/высоту в новую квадратную;
 *   - подтягиваем размеры под актуальные min/max и убираем вертикальные наложения компактацией
 *     (verticalCompactor) — это воспроизводит вид, который раскладка имела в старой системе, и
 *     чинит данные, застрявшие после промежуточной миграции.
 * Идемпотентна: данные уже актуальной версии не трогает.
 */
export function migrateWidgetsData(data: IWidgetsData): IWidgetsData {
    const version = data.gridVersion ?? 0;
    if (version >= GRID_VERSION) return data;

    let layouts = data.layouts;
    // Шаг 1 (до v2): масштаб из старой 12-колоночной сетки.
    if (version < 2 && looksLikeOldGrid(layouts)) {
        layouts = mapLayouts(layouts, scaleItems);
    }
    // Шаг 2 (до v3): подтянуть размеры под актуальные ограничения и компактнуть.
    layouts = mapLayouts(layouts, normalizeAndCompact);

    return { ...data, layouts, gridVersion: GRID_VERSION };
}
